// The protovision device label (protovision 1.0.1 §14, P-61): the text of the QR code and beside it,
//   PV1:<DEVICE_ID hex>:<lgen hex>:<K_local base32, 26 chars>:<CRC-16 hex>
// with the CRC-16/CCITT-FALSE over the three fields and their two colons, so a misread is refused here before
// anything is sent (WEB-F-DEV-02).

export type Label = {
  /** DEVICE_ID, lower-case hex, as the API takes it. */
  deviceId: string;
  deviceIdBytes: Uint8Array;
  lgen: number;
  /** The label's local key. Never leaves the browser. */
  kLocal: Uint8Array;
};

export type LabelError = "format" | "crc" | "key";

export class LabelRefused extends Error {
  readonly reason: LabelError;
  constructor(reason: LabelError) {
    super(`label refused: ${reason}`);
    this.name = "LabelRefused";
    this.reason = reason;
  }
}

/** CRC-16/CCITT-FALSE: polynomial 0x1021, initial 0xFFFF, no reflection, no final XOR. */
export function crc16(bytes: Uint8Array): number {
  let crc = 0xffff;
  for (const b of bytes) {
    crc ^= b << 8;
    for (let i = 0; i < 8; i++) crc = crc & 0x8000 ? ((crc << 1) ^ 0x1021) & 0xffff : (crc << 1) & 0xffff;
  }
  return crc;
}

const B32 = "ABCDEFGHIJKLMNOPQRSTUVWXYZ234567";

/** RFC 4648 base32 without padding, as protovision writes it. Throws on any other character. */
export function base32Decode(text: string): Uint8Array {
  const out: number[] = [];
  let bits = 0;
  let value = 0;
  for (const ch of text) {
    const v = B32.indexOf(ch);
    if (v < 0) throw new LabelRefused("key");
    value = (value << 5) | v;
    bits += 5;
    if (bits >= 8) {
      bits -= 8;
      out.push((value >> bits) & 0xff);
    }
  }
  return new Uint8Array(out);
}

export function hexToBytes(hex: string): Uint8Array {
  if (hex.length % 2 !== 0 || !/^[0-9a-f]*$/i.test(hex)) throw new Error("not hex");
  const out = new Uint8Array(hex.length / 2);
  for (let i = 0; i < out.length; i++) out[i] = parseInt(hex.slice(2 * i, 2 * i + 2), 16);
  return out;
}

export function bytesToHex(bytes: Uint8Array): string {
  return Array.from(bytes, (b) => b.toString(16).padStart(2, "0")).join("");
}

/**
 * Reads a scanned or typed label. Spaces and line breaks a person might type are ignored, and the hex and base32
 * parts are accepted in either case; the CRC is checked over the canonical text.
 */
export function parseLabel(input: string): Label {
  const parts = input.replace(/\s+/g, "").split(":");
  if (parts.length !== 5 || parts[0]?.toUpperCase() !== "PV1") throw new LabelRefused("format");
  const [, idHex = "", lgenHex = "", keyText = "", crcHex = ""] = parts;
  const id = idHex.toLowerCase();
  if (!/^[0-9a-f]{32}$/.test(id) || !/^[0-9a-f]{2}$/i.test(lgenHex) || !/^[0-9a-f]{4}$/i.test(crcHex)) {
    throw new LabelRefused("format");
  }
  const key = keyText.toUpperCase();
  const body = `${id}:${lgenHex.toLowerCase()}:${key}`;
  if (crc16(new TextEncoder().encode(body)) !== parseInt(crcHex, 16)) throw new LabelRefused("crc");
  if (key.length !== 26) throw new LabelRefused("key");
  const kLocal = base32Decode(key);
  if (kLocal.length !== 16) throw new LabelRefused("key");
  return { deviceId: id, deviceIdBytes: hexToBytes(id), lgen: parseInt(lgenHex, 16), kLocal };
}
